import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useLocation } from "wouter";
import apiClient from "../lib/apiClient ";
import { api } from "../lib/api";
import { useEffect } from "react";
import { encryptData, decryptData } from "../lib/crypto";

const BASE_URL = "https://crm.hubsyntax.com";

export function useUser() {
  const query = useQuery({
    queryKey: ["auth-user"],

    initialData: () => {
      try {
        const cached = localStorage.getItem("auth_user");
        return cached ? decryptData(cached) : undefined;
      } catch {
        return undefined;
      }
    },

    queryFn: async () => {
      const res = await fetch(api.auth.me.path, {
        credentials: "include",
      });

      if (res.status === 401) {
        localStorage.removeItem("auth_user");
        return null;
      }

      if (!res.ok) throw new Error("Failed to fetch user");

      return await res.json();
    },

    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    retry: false,
  });

  useEffect(() => {
    if (query.data) {
      const encrypted = encryptData(query.data);
      localStorage.setItem("auth_user", encrypted);
    }
  }, [query.data]);

  return query;
}

export function useLogin() {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  return useMutation({
    mutationFn: async (credentials) => {
      const res = await fetch(api.auth.login.path, {
        method: api.auth.login.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
        credentials: "include",
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 401) {
          const err = new Error("Invalid username or password");
          err.response = data;
          throw err;
        }

        if (res.status === 403 && data.activeSessionExists) {
          const err = new Error("Already logged in on another device");
          err.response = data;
          throw err;
        }

        throw new Error(data.message || "Login failed");
      }

      return data;
    },

    onSuccess: (user) => {
      const authData = {
        userId: user.userId,
        username: user.username,
        department: user.department,
      };

      const encrypted = encryptData(authData);

      localStorage.setItem("auth_user", encrypted);
      sessionStorage.setItem("auth_user", encrypted);
      queryClient.setQueryData(["auth-user"], user);

      const dept = user.department?.toLowerCase();

      if (dept === "admin") {
        setLocation("/admin");
      } else if (dept === "management") {
        setLocation("/management");
      } else {
        setLocation("/dashboard");
      }
    },

    onError: (error) => {
      console.error("Login error:", error.message);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient()
  const [, setLocation] = useLocation()

  return useMutation({
    mutationFn: async () => {
      await fetch(api.auth.logout.path, {
        method: api.auth.logout.method,
        credentials: "include",
      })
    },
    onSuccess: () => {
      sessionStorage.removeItem("auth_user");
      localStorage.removeItem("auth_user");
      localStorage.removeItem("token");
      localStorage.removeItem("session_start");
      localStorage.removeItem("projectsOrder");
      localStorage.removeItem("taskOrder");
      queryClient.removeQueries();
      setLocation("/login");
    }

  })
}

export function useUserDetails() {
  return useQuery({
    queryKey: ["user-details"],

    queryFn: async () => {
      const res = await fetch(api.auth.userDetails.path, {
        credentials: "include",
      });

      if (res.status === 401) return [];
      if (!res.ok) throw new Error("Failed to fetch user details");

      const data = await res.json();
      return data;
    },

    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,

    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,

    initialData: () => {
      try {
        const cached = localStorage.getItem("user_details");
        return cached ? JSON.parse(cached) : undefined;
      } catch {
        return undefined;
      }
    },
  });
}

export function useEmployees() {
  return useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const res = await fetch(api.auth.employees.path, {
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to fetch employees");
      return res.json();
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

export function useFilterEmployee() {
  return useQuery({
    queryKey: ["filtered-employees"],
    queryFn: async () => {
      const res = await fetch(api.auth.filterEmployee.path, {
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to fetch filtered employees");
      return res.json();
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useTotalStaff() {
  return useQuery({
    queryKey: ["total-staff"],
    queryFn: async () => {
      const res = await fetch(api.auth.totalStaff.path, {
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to fetch total staff");
      return res.json();
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await fetch(api.auth.projects.create.path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to create project");
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["projects"]);
    },
    onError: (err) => {
      console.error("Error creating project:", err);
    },
  });
}

export function useProjects(status) {
  return useQuery({
    queryKey: ["projects", status],

    queryFn: async () => {
      const url = status
        ? `${api.auth.getprojects.path}?status=${status}`
        : api.auth.getprojects.path;

      const res = await fetch(url, {
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to fetch projects");

      return api.auth.getprojects.responses[200].parse(await res.json());
    },

    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...body }) => {
      const res = await fetch(api.auth.projects.update.path(id), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update project");
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries(["projects"]);
    },
  });
}

export function useHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const res = await fetch(api.auth.holiday.create.path, {
        method: api.auth.holiday.create.method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create holiday");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["holidays"]),
  });
}

export function useTotalHolidays() {
  return useQuery({
    queryKey: ["holidays"],
    queryFn: async () => {
      const res = await fetch(api.auth.holiday.list.path, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch holidays");
      return res.json();
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useUpdateHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => {
      const res = await fetch(api.auth.holiday.update(id).path, {
        method: api.auth.holiday.update(id).method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update holiday");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["holidays"]),
  });
}

export function useDeleteHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await fetch(api.auth.holiday.delete(id).path, {
        method: api.auth.holiday.delete(id).method,
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to delete holiday");
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["holidays"]);
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, taskId, subProjectId }) => {
      const res = await fetch(
        `${api.auth.projects.update.path(projectId)}/tasks/${taskId}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ subProjectId }),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete task");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["projects"]),
  });
}

export function useCreateContracts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await fetch(api.auth.contracts.create.path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create contract");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["contracts"]),
  });
}

export function useTotalContracts() {
  return useQuery({
    queryKey: ["contracts"],
    queryFn: async () => {
      const res = await fetch(api.auth.contracts.list.path, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch contracts");
      return res.json();
    },
  });
}

export function useUpdateContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (contractData) => {
      const { id, ...payload } = contractData;
      const res = await fetch(api.auth.contracts.update.path(id), {
        method: api.auth.contracts.update.method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update contract");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["contracts"]),
  });
}

export function useDeleteContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await fetch(api.auth.contracts.delete.path(id), {
        method: api.auth.contracts.delete.method,
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete contract");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["contracts"]),
  });
}

export function useUpload() {
  const mutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(api.auth.upload.path, {
        method: api.auth.upload.method,
        body: formData,
      });

      if (!res.ok) {
        throw new Error("File upload failed");
      }

      const data = await res.json();
      return data;
    },
  });

  return mutation;
}

export function useRenameProjectStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, oldStatus, newStatus, subProjectId }) => {
      const res = await fetch(api.auth.projects.renameStatus.path(id), {
        method: api.auth.projects.renameStatus.method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          oldStatus,
          newStatus,
          subProjectId,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to rename status");
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["projects"]);
    },
  });
}

export function useDragDropTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, status, taskOrder, subProjectId }) => {
      const res = await fetch(api.auth.projects.dragDrop.path(projectId), {
        method: api.auth.projects.dragDrop.method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status, taskOrder, subProjectId }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update task order");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["projects"]),
  });
}

export function useAddComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, taskId, data }) => {
      const res = await fetch(
        api.auth.projects.comments.add.path(projectId, taskId),
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(data),
        }
      );

      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.message || "Failed to add comment");
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["projects"]);
    },
  });
}

export function useComments({ projectId, taskId, subProjectId }) {
  return useQuery({
    queryKey: ["comments", projectId, taskId, subProjectId],
    queryFn: async () => {
      const url = subProjectId
        ? `${BASE_URL}/api/auth/projects/${projectId}/tasks/${taskId}/comments?subProjectId=${subProjectId}`
        : `${BASE_URL}/api/auth/projects/${projectId}/tasks/${taskId}/comments`;

      const res = await fetch(url, { method: "GET", credentials: "include" });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Failed to fetch comments");

      return result.comments || [];
    },
    enabled: !!projectId && !!taskId,
  });
}


export const useAddProjectStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const res = await fetch(api.auth.projects.addStatus.path, {
        method: api.auth.projects.addStatus.method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to add status");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["projects"]);
    },
  });
};

export function useDeleteStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, status, subProjectId }) => {
      const res = await fetch(api.auth.projects.deleteStatus.path, {
        method: api.auth.projects.deleteStatus.method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          projectId,
          status,
          subProjectId,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to delete status");
      }

      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries(["projects"]);
    },
  });
}

export function useUpdateProjectName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, projectName, isMultiProject, status,isPinned, sharedWith, updatedBy }) => {
      const res = await fetch(api.auth.projects.projectName.path(projectId), {
        method: api.auth.projects.projectName.method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ projectName, isMultiProject, status, isPinned, sharedWith, updatedBy }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update project");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["projects"]),
  });
}

export function useAddSubProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, subProject, subStatus }) => {
      const res = await fetch(
        api.auth.projects.addSubProject.path(projectId),
        {
          method: api.auth.projects.addSubProject.method,
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ subProject, subStatus }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to add sub-project");
      }
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries(["projects"]);
    },
  });
}

export function useUpdateSubProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, subId, subProject }) => {
      const res = await fetch(`${api.auth.projects.addSubProject.path(projectId)}/${subId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ subProject }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update sub-project");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["projects"]),
  });
}

export function useDeleteSubProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, subId }) => {
      const res = await fetch(`${api.auth.projects.addSubProject.path(projectId)}/${subId}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete sub-project");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["projects"]),
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId }) => {
      const res = await fetch(api.auth.projects.delete.path(projectId), {
        method: api.auth.projects.delete.method,
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete project");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["projects"]);
    },
  });
}

export function useCreatePermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ adminBy, userId, department, management = {}, employees = {}, hr = {} }) => {
      const res = await fetch(api.auth.permissions.create.path, {
        method: api.auth.permissions.create.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adminBy, userId, department, management, employees, hr }),
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create permission");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["permissions"]);
    },
  });
}

export function useUpdatePermission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, management, employees, hr }) => {
      const res = await fetch(api.auth.permissions.update.path(id), {
        method: api.auth.permissions.update.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ management, employees, hr }),
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update permission");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["permissions"]);
    },
  });
}

export function useGetPermissions() {
  return useQuery({
    queryKey: ["permissions"],
    queryFn: async () => {
      const res = await fetch(api.auth.permissions.getByAdmin.path, {
        method: api.auth.permissions.getByAdmin.method,
        credentials: "include",
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch permissions");
      }

      return data;
    },
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ categoryName }) => {
      const res = await fetch(api.auth.category.create.path, {
        method: api.auth.category.create.method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ categoryName }),
      });

      const resData = await res.json();
      if (!res.ok) throw new Error(resData.message || "Failed to create category");
      return resData;
    },
    onSuccess: () => queryClient.invalidateQueries(["categories"]),
  });
}

export function useGetCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const res = await fetch(api.auth.category.getAll.path, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch categories");

      const json = await res.json();
      return Array.isArray(json.data) ? json.data : [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useReorderCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderedIds) => {
      const res = await fetch(api.auth.category.reorder.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ orderedIds }),
      });

      const resData = await res.json();
      if (!res.ok) throw new Error(resData.message || "Failed to reorder categories");
      return resData;
    },
    onSuccess: () => queryClient.invalidateQueries(["categories"]),
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await fetch(api.auth.category.delete.path(id), {
        method: api.auth.category.delete.method,
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete category");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["categories"]);
    },
  });
}

export function useRenameCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, categoryName }) => {
      const res = await fetch(api.auth.category.rename.path(id), {
        method: api.auth.category.rename.method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ categoryName }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to rename category");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["categories"]);
    },
  });
}

export function useImportCategoryCSV() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }) => {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(api.auth.category.importCSV.path(id), {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["categories"]);
    },
  });
}

export function useUpdateCategoryItem() {
  return useMutation({
    mutationFn: async (payload) => {
      // Ensure all required fields are present
      const { categoryId, itemId, field, value } = payload;
      if (!categoryId || !itemId || !field) {
        throw new Error("Missing required fields for updating item");
      }

      const res = await fetch(api.auth.category.update.path, {
        method: api.auth.category.update.method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ categoryId, itemId, field, value }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Update failed");
      return data;
    },
  });
}


export function useNotifications(userId) {
  return useQuery({
    queryKey: ["notifications", userId],
    queryFn: async () => {
      if (!userId) {
        return [];
      }
      const res = await fetch(
        api.auth.notifications.getAll.path(userId),
        { credentials: "include" }
      );
      if (!res.ok) {
        throw new Error("Failed to fetch notifications");
      }
      const data = await res.json();

      return data.data;
    },
    enabled: !!userId,
  });
}

export function useUnreadNotifications(userId) {
  return useQuery({
    queryKey: ["notifications", userId, "unread"],

    queryFn: async () => {

      if (!userId) {
        return [];
      }

      const res = await fetch(
        api.auth.notifications.getUnread.path(userId),
        { credentials: "include" }
      );

      if (!res.ok) {
        throw new Error("Failed to fetch unread notifications");
      }

      const data = await res.json();

      return data.data;
    },

    enabled: !!userId,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ notificationId, userId }) => {
      const res = await fetch(
        api.auth.notifications.markRead.path(notificationId, userId),
        {
          method: api.auth.notifications.markRead.method,
          credentials: "include",
        }
      );

      if (!res.ok) {
        throw new Error("Failed to mark notification as read");
      }

      const data = await res.json();

      return data;
    },

    onSuccess: (_, { notificationId, userId }) => {
      queryClient.invalidateQueries(["notifications", userId]);
      queryClient.invalidateQueries(["notifications", userId, "unread"]);
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (formData) => {
      const res = await fetch(api.auth.createUser.path, {
        method: api.auth.createUser.method,
        credentials: "include",
        body: formData,
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to create user");
      }

      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries(["myTeam"]);
    },
  });
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await fetch(api.auth.deleteUser.path(id), {
        method: api.auth.deleteUser.method,
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete employee");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["myTeam"]);
    },
  });
}

export function useSetPassword() {
  return useMutation({
    mutationFn: async ({ token, password, confirmPassword }) => {
      const res = await fetch(api.auth.setPassword.path, {
        method: api.auth.setPassword.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, password, confirmPassword }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to set password");
      }

      return data;
    },
  });
}

export function useDepartments() {
  return useQuery({
    queryKey: ["departments"],
    queryFn: async () => {
      const res = await fetch(api.auth.getDepartments.path);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch departments");
      return data.data;
    },
  });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ departmentName, createdBy }) => {
      const res = await fetch(api.auth.createDepartment.path, {
        method: api.auth.createDepartment.method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ departmentName, createdBy }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create department");
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["departments"]);
    },
  });
}

export const useDeleteDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await apiClient.delete(
        api.auth.deleteDepartment.path(id)
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["departments"]);
    },
  });
};

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(api.auth.updateUser.path(id), {
        method: api.auth.updateUser.method,
        credentials: "include",
        body: data,
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.message || "Failed to update employee");
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["employees"]);
      queryClient.invalidateQueries(["departments"]);
    },
  });
}

export function useUpdatePayroll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ employeeId, payroll }) => {
      const url = api.auth.updatepayroll.path(employeeId);

      const res = await fetch(url, {
        method: api.auth.updatepayroll.method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payroll }),
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.message || "Failed to update payroll");
      }

      return result;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["departments"],
      });
    },
  });
}

export function useCurrentUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ employeeId, data }) => {
      const res = await fetch(`${BASE_URL}/api/currentUser/${employeeId}`, {
        method: "PUT",
        credentials: "include",
        body: data,
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to update user");
      }

      const result = await res.json();
      return result.data;
    },
    onSuccess: (updatedEmployee) => {
      queryClient.invalidateQueries(["totalStaff"]);
      queryClient.invalidateQueries(["employees"]);
      console.log("User updated successfully:", updatedEmployee);
    },
    onError: (error) => {
      console.error("Error updating user:", error.message);
    },
  });
}

export function useDeletePayroll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ employeeId, payrollId }) => {
      const url = api.auth.deletePayroll.path(employeeId, payrollId);

      const res = await fetch(url, {
        method: api.auth.deletePayroll.method,
        credentials: "include",
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.message || "Failed to delete payroll entry");
      }

      return result;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["departments"],
      });
    },
  });
}

export function useCreateLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await fetch(api.auth.leave.create.path, {
        method: api.auth.leave.create.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to submit leave");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leaves"] });
    },
  });
}

export function useLeaves(userId, isHRorAdmin) {
  return useQuery({
    queryKey: ["leaves", userId],
    queryFn: async () => {
      const url = isHRorAdmin
        ? api.auth.leave.get.path()
        : api.auth.leave.get.path({ userId });
      const res = await fetch(url);
      const data = await res.json();
      if (!data.success) throw new Error("Failed to fetch leaves");
      return data.leaves;
    },
    staleTime: 0,
  });
}

export function useUpdateLeave() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, leaveId, status, hrReply }) => {
      const res = await fetch(api.auth.leave.update.path({ userId, leaveId }), {
        method: api.auth.leave.update.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, hrReply }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to update leave");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leaves"] });
    },
  });
}

export function useExpenses() {
  return useQuery({
    queryKey: ["expenses"],
    queryFn: async () => {
      const res = await fetch(api.auth.expenses.getAll.path, {
        method: api.auth.expenses.getAll.method,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch expenses");
      return data.data;
    },
  });
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (expenseData) => {
      const res = await fetch(api.auth.expenses.add.path, {
        method: api.auth.expenses.add.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(expenseData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create expense");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
    },
  });
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expenseData }) => {
      const res = await fetch(api.auth.expenses.update.path(id), {
        method: api.auth.expenses.update.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(expenseData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update expense");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
    },
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (expenseId) => {
      const res = await fetch(api.auth.expenses.delete.path(expenseId), {
        method: api.auth.expenses.delete.method,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete expense");

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
    },
  });
}

export function useGetHiring() {
  return useQuery({
    queryKey: ["hirings"],
    queryFn: async () => {
      const res = await fetch(api.auth.hiring.getAll.path, { method: api.auth.hiring.getAll.method });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch hirings");
      return data;
    },
  });
}

export function useCreateHiring() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData) => {
      const res = await fetch(api.auth.hiring.create.path, {
        method: api.auth.hiring.create.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create hiring");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hirings"] });
    },
  });
}

export function useUpdateHiring() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const res = await fetch(api.auth.hiring.update.path(id), {
        method: api.auth.hiring.update.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseData = await res.json();

      if (!res.ok) {
        throw new Error(responseData.message || "Failed to update hiring");
      }

      return responseData;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hirings"] });
    },
  });
}

export function useDeleteHiring() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await fetch(api.auth.hiring.delete.path(id), {
        method: api.auth.hiring.delete.method,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete hiring");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hirings"] });
    },
  });
}

export function useGetLeads() {
  return useQuery({
    queryKey: ["leads"],
    queryFn: async () => {
      const res = await fetch(api.auth.leads.getAll.path, {
        method: api.auth.leads.getAll.method,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch leads");

      return data;
    },
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (formData) => {
      const res = await fetch(api.auth.leads.create.path, {
        method: api.auth.leads.create.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create lead");

      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, ...formData }) => {
      const res = await fetch(api.auth.leads.update.path(leadId), {
        method: api.auth.leads.update.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || "Failed to update lead");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["wonleads"]);
    },
  });
}

export function useAddLeadsComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData) => {
      const res = await fetch(api.auth.leads.addComment.path, {
        method: api.auth.leads.addComment.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (!res.ok)
        throw new Error(data.error || data.message || "Failed to update comment");

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["wonleads"]);
    },
  });
}

export function useUpdateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, ...formData }) => {
      const res = await fetch(api.auth.leads.updateComment.path, {
        method: api.auth.leads.updateComment.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (!res.ok)
        throw new Error(data.error || data.message || "Failed to update comment");

      return data;
    },
    onSuccess: () => {
    },
  });
}

export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ commentId }) => {
      const res = await fetch(api.auth.leads.deleteComment.path, {
        method: api.auth.leads.deleteComment.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ commentId }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.error || data.message || "Failed to delete comment"
        );
      }

      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["wonleads"]);
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, leadId }) => {
      const res = await fetch(
        api.auth.leads.delete.path(userId, leadId),
        {
          method: api.auth.leads.delete.method,
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete lead");

      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });
}

export function useCreateLetters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData) => {
      const res = await fetch(api.auth.letters.create.path, {
        method: api.auth.letters.create.method,
        body: formData,
        credentials: "include",
      });

      const resData = await res.json();
      if (!res.ok) throw new Error(resData.message || "Failed to create letter");
      return resData;
    },
    onSuccess: () => queryClient.invalidateQueries(["letters"]),
  });
}

export function useGetLetters() {
  return useQuery({
    queryKey: ["letters"],
    queryFn: async () => {
      const res = await fetch(api.auth.letters.getall.path, {
        method: api.auth.letters.getall.method,
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to fetch letters");

      const json = await res.json();
      return Array.isArray(json) ? json : [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateLetter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, formData }) => {
      const res = await fetch(api.auth.letters.update.path(id), {
        method: api.auth.letters.update.method,
        body: formData,
        credentials: "include",
      });

      const resData = await res.json();
      if (!res.ok) throw new Error(resData.message || "Failed to update letter");
      return resData;
    },
    onSuccess: () => queryClient.invalidateQueries(["letters"]),
  });
}

export function useDeleteLetter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await fetch(api.auth.letters.delete.path(id), {
        method: api.auth.letters.delete.method,
        credentials: "include",
      });
      const resData = await res.json();
      if (!res.ok) throw new Error(resData.message || "Failed to delete letter");
      return resData;
    },
    onSuccess: () => queryClient.invalidateQueries(["letters"]),
  });
}

export function useSendLetters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const res = await fetch(api.auth.letters.letterSend.path, {
        method: api.auth.letters.letterSend.method,

        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
        credentials: "include",
      });
      const resData = await res.json();

      if (!res.ok) {
        throw new Error(resData.message || "Failed to send letter");
      }
      return resData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["letters"]);
    },
  });
}

export function useLoginDetials() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, loginDetials, subProjectId }) => {
      const res = await fetch(api.auth.projects.loginDetials.path(projectId), {
        method: api.auth.projects.loginDetials.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ loginDetials, subProjectId }),
        credentials: "include",
      });

      const resData = await res.json();

      if (!res.ok) {
        throw new Error(resData.message || "Failed to update login details");
      }

      return resData;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["projects"]);
      queryClient.invalidateQueries(["project", data.data.id]);
    },
  });
}

export function useDeleteLeave() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leaveId) => {
      const res = await fetch(`${BASE_URL}/api/leave/${leaveId}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to delete leave");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leaves"] });
    },
  });
}